import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface RoleGuardProps {
  children: ReactNode;
  /** Render only if the user is authenticated, regardless of role. */
  authenticated?: boolean;
  /** Render only if the user is NOT authenticated (anonymous / signed-out). */
  anonymous?: boolean;
  staff?: boolean;
  admin?: boolean;
  superAdmin?: boolean;
  /** Render only if the user holds the given capability, e.g. {resource:'reporting', action:'export'}. */
  can?: { resource: string; action: string };
  /** Fallback to render when access is denied. */
  fallback?: ReactNode;
}

export function RoleGuard({
  children,
  authenticated,
  anonymous,
  staff,
  admin,
  superAdmin,
  can: capability,
  fallback = null,
}: RoleGuardProps) {
  const { isAuthenticated, isStaff, isAdmin, isSuperAdmin, can } = useAuth();
  if (anonymous && isAuthenticated) return <>{fallback}</>;
  if (authenticated && !isAuthenticated) return <>{fallback}</>;
  if (superAdmin && !isSuperAdmin()) return <>{fallback}</>;
  if (admin && !isAdmin() && !isSuperAdmin()) return <>{fallback}</>;
  if (staff && !isStaff()) return <>{fallback}</>;
  if (capability && !can(capability.resource, capability.action)) return <>{fallback}</>;
  return <>{children}</>;
}

export default RoleGuard;
